<template>
  <div class="video-player-page">

    <!-- 视频播放页面 -->
    <div class="video-player-page-content">

      <!-- 顶部导航栏 -->
      <div class="player-header">
        <div class="back-button" @click="goBack">
          <i class="icon-back"></i>
        </div>
        <div class="video-title">{{ videoData.zhName }}</div>
        <div class="report-button" @click="showReportDialog">
          <i class="icon-report"></i>
          <span>举报内容</span>
        </div>
      </div>

      <div class="player-content">
        <!-- 视频播放器 -->
        <div class="player-container">
          <video ref="videoPlayer" class="video-element" :src="videoData.videoUrl" :poster="videoData.coverImage"
            controls autoplay @play="onVideoPlay" @pause="onVideoPause" @timeupdate="onTimeUpdate"></video>

          <!-- 自定义控制栏（可选） -->
          <div class="custom-controls" v-if="showControls">
            <div class="progress-bar">
              <div class="progress-background"></div>
              <div class="progress-current" :style="{ width: progressPercentage + '%' }"></div>
              <div class="progress-handle" :style="{ left: progressPercentage + '%' }"></div>
            </div>

            <div class="control-buttons">
              <button class="play-button" @click="togglePlay">
                <i :class="isPlaying ? 'icon-pause' : 'icon-play'"></i>
              </button>
              <div class="time-display">{{ currentTime }} / {{ totalTime }}</div>
              <button class="fullscreen-button" @click="toggleFullscreen">
                <i class="icon-fullscreen"></i>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- 选集列表 -->
    <div class="episode-section">
      <h3 class="resolution-title">选择清晰度：</h3>
      <div class="resolution-selector" v-if="availableResolutions.length > 0">
        <div class="resolution-options">
          <div 
            v-for="resolution in availableResolutions" 
            :key="resolution.value"
            class="resolution-option"
            :class="{ 'active': currentResolution === resolution.value, 'vip-required': resolution.requireVip && !isVip }"
            @click="changeResolution(resolution)"
          >
            {{ resolution.label }}
            <span class="vip-tag" v-if="resolution.requireVip">VIP</span>
          </div>
        </div>
      </div>
      
      <div class="episode-header">
        <h3 class="section-title">选集</h3>
      </div>
      
      <div class="episode-grid">
        <div v-for="(episode, index) in videoData.episodes" :key="episode.id" class="episode-item"
          :class="{ 'active': currentEpisodeIndex === index }" @click="playEpisode(index, episode.id)">
          <div class="episode-image">
            <img :src="IMAGE_BASE_URL + videoData.secondPoster" alt="" mode="widthFix">
            <div class="episode-overlay">
              <div class="play-icon" v-if="currentEpisodeIndex !== index">
                <i class="play-button"></i>
              </div>
              <!-- 当前播放的波浪效果 -->
              <div class="wave-effect" v-if="currentEpisodeIndex === index">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          <div class="episode-title">{{ episode.title }}</div>
        </div>
      </div>
    </div>

    <!-- 举报对话框 -->
    <report-dialog :visible="reportDialogVisible" :resource-type="'video'" :resource-id="videoId"
      @cancel="reportDialogVisible = false" @success="handleReportSuccess" />
      
    <!-- VIP订阅对话框 -->
    <div class="vip-dialog" v-if="vipDialogVisible">
      <div class="vip-dialog-content">
        <div class="vip-dialog-header">
          <h3>开通VIP，尊享高清观影体验</h3>
          <div class="close-button" @click="vipDialogVisible = false">×</div>
        </div>
        
      
        
        <div class="vip-plans">
          <div 
            v-for="plan in vipRules" 
            :key="plan.id" 
            class="vip-plan"
            :class="{ 'selected': selectedVipPlan === plan.id }"
            @click="selectVipPlan(plan.id)"
          >
            <div class="plan-name">{{ plan.zhName }}</div>
            <div class="plan-price">¥{{ plan.price/100 }}</div>
            <div class="plan-original-price" v-if="plan.originalPrice > plan.price">原价: ¥{{ plan.originalPrice/100 }}</div>
            <div class="plan-duration">{{ plan.duration }}天</div>
            <div class="best-value" v-if="plan.flag === 'best_value'">超值优惠</div>
          </div>
        </div>

        <div class="vip-benefits">
          <div class="benefit-item">
            <div class="benefit-icon">🎬</div>
            <div class="benefit-text">1080P超高清视频</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">🚀</div>
            <div class="benefit-text">极速观影无广告</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">💎</div>
            <div class="benefit-text">尊享会员专属内容</div>
          </div>
        </div>
        
        <div class="agreement-section">
          <label class="agreement-checkbox">
            <input type="checkbox" v-model="agreementAccepted">
            <span>我已阅读并同意</span>
          </label>
          <a href="https://subat.gxxushang.com/app/MembershipServiceAgreement.html"  target="_blank" class="agreement-link">《VIP会员服务协议》</a>
        </div>
        
        <button 
          class="purchase-button" 
          :disabled="!selectedVipPlan || !agreementAccepted"
          @click="purchaseVip"
        >
          立即开通
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getDetail, getEpisodeList, getVipRuleList, getVipRecord } from '../api';
import { IMAGE_BASE_URL } from '../utils/constants';
import ReportDialog from '../components/ReportDialog.vue';

export default {
  name: 'VideoPlayer',
  components: {
    ReportDialog
  },
  props: {
    videoId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      videoData: {},
      isPlaying: false,
      currentTime: '00:00',
      totalTime: '00:00',
      progressPercentage: 0,
      currentEpisodeIndex: 0,
      showControls: false,
      IMAGE_BASE_URL,
      reportDialogVisible: false,
      availableResolutions: [],
      currentResolution: null,
      isVip: false,
      vipDialogVisible: false,
      vipRules: [],
      selectedVipPlan: null,
      agreementAccepted: false
    };
  },
  mounted() {
    // 初始化视频播放器
    const videoElement = this.$refs.videoPlayer;
    if (videoElement) {
      videoElement.addEventListener('loadedmetadata', this.onVideoLoaded);

      // 设置总时长
      if (videoElement.duration) {
        this.totalTime = this.formatTime(videoElement.duration);
      }
    }

    // 如果有传入的视频ID，加载对应数据
    if (this.videoId) {
      this.fetchVideoData(this.videoId);
    }
    
    // 获取用户VIP状态
    this.checkUserVipStatus();
  },
  beforeUnmount() {
    // 清理事件监听
    const videoElement = this.$refs.videoPlayer;
    if (videoElement) {
      videoElement.removeEventListener('loadedmetadata', this.onVideoLoaded);
    }
  },
  methods: {
    goBack() {
      // 通知父组件返回
      this.$emit('close-video');
      // 清除视频ID
      this.$emit('update:videoId', null);
    },
    showReportDialog() {
      // 显示举报对话框
      this.reportDialogVisible = true;
    },
    handleReportSuccess() {
      // 举报成功后关闭对话框
      this.reportDialogVisible = false;
      // 可以添加一个成功提示
      alert('感谢您的举报，我们会尽快处理');
    },
    onVideoLoaded(event) {
      const video = event.target;
      this.totalTime = this.formatTime(video.duration);
    },
    onVideoPlay() {
      this.isPlaying = true;
    },
    onVideoPause() {
      this.isPlaying = false;
    },
    onTimeUpdate(event) {
      const video = event.target;
      this.currentTime = this.formatTime(video.currentTime);
      this.progressPercentage = (video.currentTime / video.duration) * 100;
    },
    togglePlay() {
      const video = this.$refs.videoPlayer;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    toggleFullscreen() {
      const video = this.$refs.videoPlayer;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        video.requestFullscreen();
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    async playEpisode(index, episodeId) {
      // 添加参数验证
      if (episodeId === undefined) {
        console.error('剧集ID未定义，无法播放');
        return;
      }

      console.log(index, 'index');
      console.log(episodeId, 'episodeId');

      if (index === this.currentEpisodeIndex) return;

      this.currentEpisodeIndex = index;

      try {
        // 添加加载状态
        this.isLoading = true;

        // 使用当前分辨率或默认720p
        const resolution = this.currentResolution || 720;
        const episodeList = await getEpisodeList(episodeId, resolution);
        this.videoData.videoUrl = episodeList.data.playConfig.url;
        
        // 处理可用分辨率
        this.processAvailableResolutions(episodeList.data.playConfig.resolution);

        // 确保索引有效
        if (!this.videoData.episodes[index]) {
          console.error(`索引 ${index} 超出范围`);
          return;
        }

        const episode = this.videoData.episodes[index];
        console.log(episode, 'episode');

        // 使用setTimeout确保DOM更新后再设置时间和播放
        setTimeout(() => {
          const video = this.$refs.videoPlayer;
          if (video) {
            // 尝试播放，并处理可能的错误
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.error('视频播放失败:', error);
                // 不自动播放，让用户手动点击播放按钮
              });
            }
          }
        }, 100);
      } catch (error) {
        console.error('加载剧集失败:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchVideoData(videoId) {
      try {
        console.log('获取视频数据:', videoId);
        
        // 先获取用户VIP状态
        await this.checkUserVipStatus();
        console.log('用户VIP状态:', this.isVip);
        
        const response = await getDetail(videoId);
        this.videoData = response.data;
        console.log('视频数据获取成功, 剧集数量:', this.videoData.episodes.length);
        
        if (!this.videoData.episodes || this.videoData.episodes.length === 0) {
          console.error('视频没有剧集数据');
          return;
        }
        
        let episodeId = this.videoData.episodes[0].id;
        console.log('加载第一集, ID:', episodeId);

        // 根据VIP状态选择分辨率
        let initialResolution = this.isVip ? 1080 : 720;
        console.log('初始分辨率:', initialResolution, 'VIP状态:', this.isVip);
        
        let episodeList = await getEpisodeList(episodeId, initialResolution);
        console.log('获取到的播放配置:', episodeList);
        
        if (!episodeList || !episodeList.data || !episodeList.data.playConfig) {
          console.error('获取播放配置失败');
          return;
        }
        
        // 设置视频URL
        this.videoData.videoUrl = episodeList.data.playConfig.url;
        console.log('设置视频URL:', this.videoData.videoUrl);
        
        // 处理可用分辨率
        if (episodeList.data.playConfig.resolution) {
          this.processAvailableResolutions(episodeList.data.playConfig.resolution);
          console.log('可用分辨率:', this.availableResolutions);
        } else {
          console.warn('没有获取到分辨率信息');
        }
      } catch (error) {
        console.error('获取视频数据失败:', error);
      }
    },
    processAvailableResolutions(resolutions) {
      if (!resolutions || !Array.isArray(resolutions)) {
        console.warn('无效的分辨率数据:', resolutions);
        this.availableResolutions = [];
        return;
      }
      
      console.log('处理分辨率列表:', resolutions);
      
      // 将分辨率数组转换为对象数组，并标记需要VIP的分辨率
      this.availableResolutions = resolutions.map(res => {
        const requireVip = parseInt(res) >= 1080; // 1080及以上分辨率需要VIP
        return {
          value: parseInt(res),
          label: `${res}p`,
          requireVip: requireVip
        };
      });
      
      // 排序，让低分辨率在前，高分辨率在后
      this.availableResolutions.sort((a, b) => a.value - b.value);
      
      console.log('处理后的分辨率:', this.availableResolutions);
      
      // 设置当前分辨率
      if (this.currentResolution === null && this.availableResolutions.length > 0) {
        // 如果是VIP用户，使用最高分辨率
        if (this.isVip) {
          const highestRes = this.availableResolutions[this.availableResolutions.length - 1];
          this.currentResolution = highestRes.value;
          console.log('VIP用户，选择最高分辨率:', this.currentResolution);
        } else {
          // 查找不需要VIP的最高分辨率
          const nonVipResolutions = this.availableResolutions.filter(res => !res.requireVip);
          if (nonVipResolutions.length > 0) {
            const highestNonVipRes = nonVipResolutions[nonVipResolutions.length - 1];
            this.currentResolution = highestNonVipRes.value;
            console.log('非VIP用户，选择最高非VIP分辨率:', this.currentResolution);
          } else {
            // 如果所有分辨率都需要VIP，则选择最低的
            this.currentResolution = this.availableResolutions[0].value;
            console.log('所有分辨率都需要VIP，选择最低分辨率:', this.currentResolution);
          }
        }
      }
    },
    async changeResolution(resolution) {
      console.log('尝试切换到分辨率:', resolution.value, '是否需要VIP:', resolution.requireVip, '用户是否VIP:', this.isVip);
      
      // 如果需要VIP但用户不是VIP，再次检查VIP状态
      if (resolution.requireVip && !this.isVip) {
        // 再次检查VIP状态，避免用户状态已变更
        await this.checkUserVipStatus();
        this.showVipDialog();
        
        // 再次检查，如果仍然不是VIP会员，显示VIP订阅对话框
        if (!this.isVip) {
          console.log('用户不是VIP，显示订阅对话框');
          this.vipDialogVisible = true;
          return;
        }
      }
      
      // 如果已经是当前分辨率，不做任何操作
      if (this.currentResolution === resolution.value) {
        console.log('已经是当前分辨率，无需切换');
        return;
      }
      
      try {
        // 显示加载状态
        this.isLoading = true;
        console.log('开始切换分辨率到:', resolution.value);
        
        // 记录当前分辨率
        this.currentResolution = resolution.value;
        
        // 获取当前播放时间位置
        const currentTime = this.$refs.videoPlayer ? this.$refs.videoPlayer.currentTime : 0;
        
        // 重新加载当前剧集
        const episodeId = this.videoData.episodes[this.currentEpisodeIndex].id;
        console.log('获取剧集:', episodeId, '分辨率:', resolution.value);
        const episodeList = await getEpisodeList(episodeId, resolution.value);
        
        if (!episodeList || !episodeList.data || !episodeList.data.playConfig || !episodeList.data.playConfig.url) {
          console.error('获取播放配置失败:', episodeList);
          return;
        }
        
        // 更新视频URL
        this.videoData.videoUrl = episodeList.data.playConfig.url;
        console.log('新的视频URL:', this.videoData.videoUrl);
        
        // 使用setTimeout确保DOM更新后再设置时间和播放
        setTimeout(() => {
          const video = this.$refs.videoPlayer;
          if (video) {
            video.currentTime = currentTime;
            console.log('设置视频当前时间:', currentTime);
            
            // 尝试播放，并处理可能的错误
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.error('视频播放失败:', error);
                // 不自动播放，让用户手动点击播放按钮
              });
            }
          } else {
            console.error('找不到视频元素');
          }
        }, 100);
      } catch (error) {
        console.error('切换分辨率失败:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async showVipDialog() {
      try {
        // 如果对话框已经显示，不要重复操作
        if (this.vipDialogVisible) {
          return;
        }
        
        // 再次检查VIP状态，避免用户状态已变更
        await this.checkUserVipStatus();
        
        // 如果用户已经是VIP会员，不需要显示订阅对话框
        if (this.isVip) {
          console.log('用户已是VIP会员，无需显示订阅对话框');
          return;
        }
        
        // 获取VIP规则列表
        const response = await getVipRuleList();
        
        if (response && response.data) {
          this.vipRules = response.data;
          console.log(this.vipRules, 'vipRules');
          
          // 如果有规则，默认选中第一个
          if (this.vipRules.length > 0) {
            this.selectedVipPlan = this.vipRules[0].id;
          } else {
            console.log('没有可用的VIP规则');
          }
        } else {
          console.error('VIP规则列表响应格式不正确:', response);
          this.vipRules = [];
        }
        
        // 显示对话框
        this.vipDialogVisible = true;
      } catch (error) {
        console.error('获取VIP规则列表失败:', error);
        // 即使出错，也显示对话框
        this.vipDialogVisible = true;
      }
    },
    selectVipPlan(planId) {
      this.selectedVipPlan = planId;
    },
    purchaseVip() {
      // 实现购买VIP的逻辑
      console.log('购买VIP:', this.selectedVipPlan);
      this.vipDialogVisible = false;
    },
    async checkUserVipStatus() {
      try {
        // 只使用getVipRecord API来获取VIP状态
        const vipResponse = await getVipRecord();
        
        // 检查返回的结构
        if (vipResponse && vipResponse.data) {
          this.isVip = vipResponse.data.isVip === true;
          console.log('VIP状态:', this.isVip);
          
          // 如果是VIP用户，显示过期时间
          if (this.isVip && vipResponse.data.expiredAt) {
            const expireDate = new Date(vipResponse.data.expiredAt);
            console.log(`VIP会员到期时间: ${expireDate.toLocaleDateString()}`);
          }
        } else {
          console.log('VIP响应数据结构不符合预期:', vipResponse);
          this.isVip = false;
        }
      } catch (error) {
        console.error('获取用户VIP状态失败:', error);
        this.isVip = false;
      }
    }
  }
};
</script>

<style scoped>
.video-player-page {
  display: flex;
  /* flex-direction: column; */
  height: calc(100vh - 100px);

  background-color: #f5f5f5;
  overflow: hidden;
}

.video-player-page-content {
  /* width: 80%; */
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  margin: 10px;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  /* overflow: hidden; */
  /* height: calc(100% - 20px); */
  height: fit-content;
}

/* 顶部导航栏 */
.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 20px 10px;
  background-color: #fff;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.back-button {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.05);
}

.icon-back {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23333' viewBox='0 0 24 24'%3E%3Cpath d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.video-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  flex: 1;
  margin: 0 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.report-button {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 16px;
  cursor: pointer;
}

.icon-report {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 5px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23666' viewBox='0 0 24 24'%3E%3Cpath d='M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.player-content {
  flex: 0 0 auto;
}

/* 视频播放器 */
.player-container {
  position: relative;
  width: 100%;
  background-color: #000;
  height: 0;
  padding-bottom: 56.25%;
  /* 16:9 比例 */
  margin-bottom: 15px;
  border-radius: 4px;
}

.video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

/* 自定义控制栏 */
.custom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

.progress-bar {
  position: relative;
  height: 4px;
  margin-bottom: 10px;
  cursor: pointer;
}

.progress-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.progress-current {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #4169e1;
  border-radius: 2px;
}

.progress-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #4169e1;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.control-buttons {
  display: flex;
  align-items: center;
}

.play-button,
.fullscreen-button {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-play {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.icon-pause {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 19h4V5H6v14zm8-14v14h4V5h-4z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.icon-fullscreen {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.time-display {
  flex: 1;
  color: #fff;
  font-size: 12px;
  text-align: center;
}

/* 选集列表 */
.episode-section {
  background-color: #fff;
  margin: 10px 0;
  /* padding: 15px ; */
  overflow-y: auto;
  /* flex: 1; */
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.resolution-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-top: 0;
  position: sticky;
  margin: 20px;
  font-weight: bold;
}

.episode-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* padding: 10px 10px 20px 10px; */
  background-color: #fff;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-top: 0;
  position: sticky;
  top: 0;
  background-color: #fff;
  padding: 20px 10px 0px 20px;
  z-index: 1;
}

.episode-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 20px;
  /* overflow-y: auto; */
}

.episode-item {
  width: 150px;
  cursor: pointer;
  transition: all 0.3s;
}

.episode-item:hover {
  transform: translateY(-5px);
}

.episode-image {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  /* 16:9 比例 */
  border-radius: 4px;
  overflow: hidden;
  background-color: #f0f0f0;
}

.episode-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.episode-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s;
}

.episode-item:hover .episode-overlay {
  background-color: rgba(0, 0, 0, 0.4);
}

.play-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
}

.play-button {
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 8px 0 8px 16px;
  border-color: transparent transparent transparent #4169e1;
  margin-left: 4px;
}

.episode-title {
  margin-top: 8px;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  direction: rtl;
}

.episode-item.active .episode-image {
  box-shadow: 0 0 0 2px #4169e1;
}

/* 波浪效果 */
.wave-effect {
  position: absolute;
  bottom: 10px;
  left: 0;
  width: 100%;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.wave-effect span {
  display: inline-block;
  width: 4px;
  height: 20px;
  margin: 0 2px;
  background-color: #fff;
  animation: wave 1.2s infinite ease-in-out;
}

.wave-effect span:nth-child(2) {
  animation-delay: 0.2s;
}

.wave-effect span:nth-child(3) {
  animation-delay: 0.4s;
}

.wave-effect span:nth-child(4) {
  animation-delay: 0.6s;
}

@keyframes wave {

  0%,
  40%,
  100% {
    transform: scaleY(0.4);
  }

  20% {
    transform: scaleY(1);
  }
}

/* 分辨率选择器 */
.resolution-selector {
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 10px;
}

.resolution-options {
  display: flex;
  gap: 8px;
}

.resolution-option {
  padding: 8px 10px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  color: #333;
  border: 1px solid #eee;
}

.resolution-option:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.resolution-option.active {
  background-color: #4169e1;
  color: #fff;
  border-color: #4169e1;
}

.resolution-option.vip-required {
  color: #333;
  border: 1px solid #ffd700;
  background-color: rgba(255, 215, 0, 0.1);
}

.resolution-option.vip-required.active {
  background-color: #ffd700;
  color: #333;
}

.vip-tag {
  font-size: 10px;
  background-color: #ffd700;
  color: #333;
  padding: 0 4px;
  border-radius: 2px;
  margin-left: 4px;
}

/* VIP对话框 */
.vip-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.vip-dialog-content {
  width: 90%;
  max-width: 500px;
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: vipDialogFadeIn 0.3s ease;
}

@keyframes vipDialogFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.vip-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 15px;
}

.vip-dialog-header h3 {
  font-size: 20px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 24px;
  color: #666;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: rotate(90deg);
}

.vip-benefits {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 40px;
  margin-top: 40px;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.benefit-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.benefit-text {
  font-size: 12px;
  color: #666;
}

.vip-plans {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.vip-plan {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.vip-plan:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.vip-plan.selected {
  border-color: #4169e1;
  background-color: rgba(65, 105, 225, 0.05);
}

.plan-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.plan-price {
  font-size: 20px;
  font-weight: 700;
  color: #4169e1;
  margin-bottom: 4px;
}

.plan-original-price {
  font-size: 12px;
  color: #999;
  text-decoration: line-through;
  margin-bottom: 4px;
}

.plan-duration {
  font-size: 14px;
  color: #666;
}

.best-value {
  font-size: 12px;
  color: #ffd700;
  background-color: rgba(255, 215, 0, 0.1);
  padding: 2px 4px;
  border-radius: 2px;
  margin-top: 4px;
}

.agreement-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  justify-content: center;
}

.agreement-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.agreement-checkbox input {
  margin-right: 5px;
}

.agreement-link {
  color: #4169e1;
  text-decoration: none;
}

.purchase-button {
  width: 100%;
  padding: 12px;
  background-color: #4169e1;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.purchase-button:hover {
  background-color: #3258d3;
}

.purchase-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>